import apiClient from '../axios.js'

/**
 * ChatMate AI 客服 API
 */
export const chatmateService = {
  /**
   * 發送對話
   * @param {Object} params - 對話參數
   * @param {string} params.message - 用戶訊息
   * @param {string} params.sessionId - 會話 ID（選填）
   * @param {Object} params.context - 上下文資訊（選填）
   * @returns {Promise<Object>} 對話回應
   */
  chat: async (params) => {
    const response = await apiClient.post('/chatmate/chat', params)
    return response
  },

  /**
   * 取得對話列表
   * @param {Object} params - 查詢參數
   * @param {number} params.page - 頁碼
   * @param {number} params.pageSize - 每頁數量
   * @param {string} params.sessionId - 會話 ID（篩選）
   * @param {string} params.memberId - 會員 ID（篩選）
   * @param {string} params.lineUserId - LINE User ID（篩選）
   * @param {string} params.startDate - 開始日期（YYYY-MM-DD）
   * @param {string} params.endDate - 結束日期（YYYY-MM-DD）
   * @param {string} params.search - 搜尋關鍵字
   * @param {string} params.sortBy - 排序欄位
   * @param {string} params.sortOrder - 排序順序
   * @returns {Promise<Object>} 對話列表
   */
  getConversations: async (params = {}) => {
    const response = await apiClient.get('/chatmate/conversations', { params })
    return response
  },

  /**
   * 取得對話詳情
   * @param {string} sessionId - 會話 ID
   * @returns {Promise<Object>} 對話詳情
   */
  getConversation: async (sessionId) => {
    const response = await apiClient.get(`/chatmate/conversations/${sessionId}`)
    return response
  },

  /**
   * 上傳知識庫檔案
   * @param {FormData} formData - 表單資料（包含 file, title, tags, metadata）
   * @returns {Promise<Object>} 上傳結果
   */
  uploadKnowledge: async (formData) => {
    const response = await apiClient.post('/chatmate/knowledge/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response
  },

  /**
   * 取得知識庫列表
   * @param {Object} params - 查詢參數
   * @param {number} params.page - 頁碼
   * @param {number} params.pageSize - 每頁數量
   * @param {string} params.status - 狀態（active, inactive, deleted）
   * @param {string} params.trainingStatus - 訓練狀態（pending, processing, completed, failed）
   * @param {string} params.search - 搜尋關鍵字
   * @param {Array<string>} params.tags - 標籤篩選
   * @param {string} params.sortBy - 排序欄位
   * @param {string} params.sortOrder - 排序順序
   * @returns {Promise<Object>} 知識庫列表
   */
  getKnowledgeList: async (params = {}) => {
    const response = await apiClient.get('/chatmate/knowledge', { params })
    return response
  },

  /**
   * 取得知識庫詳情
   * @param {string} id - 知識庫 ID
   * @returns {Promise<Object>} 知識庫詳情
   */
  getKnowledge: async (id) => {
    const response = await apiClient.get(`/chatmate/knowledge/${id}`)
    return response
  },

  /**
   * 手動同步 FAQ 至知識庫（刪除重建）
   * @returns {Promise<Object>} 同步結果（sync_id, status, total_items）
   */
  syncFaqToKnowledge: async () => {
    const response = await apiClient.post('/chatmate/knowledge/sync/faq')
    return response
  },

  /**
   * 刪除知識庫
   * @param {string} id - 知識庫 ID
   * @returns {Promise<Object>} 刪除結果
   */
  deleteKnowledge: async (id) => {
    const response = await apiClient.delete(`/chatmate/knowledge/${id}`)
    return response
  },

  /**
   * 手動觸發知識庫訓練（用於重試失敗的訓練）
   * @param {string} id - 知識庫 ID
   * @returns {Promise<Object>} 訓練結果
   */
  trainKnowledge: async (id) => {
    const response = await apiClient.post(`/chatmate/knowledge/${id}/train`)
    return response
  },

  /**
   * 取得知識庫原始檔案下載連結（5 分鐘有效的 Presigned URL）
   * @param {string} id - 知識庫 ID
   * @returns {Promise<Object>} 包含 url 與 expiresIn 的物件
   */
  getKnowledgeDownloadUrl: async (id) => {
    const response = await apiClient.get(`/chatmate/knowledge/${id}/download`)
    return response
  },

  /**
   * 取得訂閱狀態
   * @returns {Promise<Object>} 訂閱狀態
   */
  getSubscription: async () => {
    const response = await apiClient.get('/chatmate/subscription')
    return response
  },

  /**
   * 更新訂閱（設定信用卡）
   * @param {Object} data - 訂閱資料
   * @returns {Promise<Object>} 更新結果
   */
  updateSubscription: async (data) => {
    const response = await apiClient.put('/chatmate/subscription', data)
    return response
  },

  /**
   * 取得配額設定
   * @returns {Promise<Object>} 配額設定
   */
  getQuotaSettings: async () => {
    const response = await apiClient.get('/chatmate/quota')
    return response
  },

  /**
   * 更新配額設定
   * @param {Object} settings - 配額設定
   * @returns {Promise<Object>} 更新結果
   */
  updateQuotaSettings: async (settings) => {
    const response = await apiClient.put('/chatmate/quota', settings)
    return response
  },

  /**
   * 取得使用量統計
   * @param {Object} params - 查詢參數
   * @param {string} params.startDate - 開始日期（YYYY-MM-DD）
   * @param {string} params.endDate - 結束日期（YYYY-MM-DD）
   * @returns {Promise<Object>} 使用量統計
   */
  getUsageStats: async (params) => {
    const response = await apiClient.get('/chatmate/usage', { params })
    return response
  },

  /**
   * 取得 AI 設定
   * @returns {Promise<Object>} AI 設定
   */
  getAISettings: async () => {
    const response = await apiClient.get('/chatmate/settings')
    return response
  },

  /**
   * 更新 AI 設定
   * @param {Object} settings - AI 設定
   * @returns {Promise<Object>} 更新結果
   */
  updateAISettings: async (settings) => {
    const response = await apiClient.put('/chatmate/settings', settings)
    return response
  }
}
